// src/pages/Login.jsx
// v7 — NEW (22 Jul 2026): small privacy-acknowledgment line added
// directly under the Google Sign-In button — matches the exact
// pattern confirmed live on claude.ai's own sign-in page (checked
// directly). Not a paragraph, not a data-use explanation — just a
// one-line acknowledgment linking to /privacy, invisible unless
// someone's actually on this screen. This replaces the now-removed
// homepage text (Home.jsx v5) as the sole remaining piece addressing
// Google's OAuth verification requirement, correctly scoped to the
// actual sign-in moment instead of the homepage.
// v6 — Added Role Selector step (Customer / Reseller / Wholesaler) shown right after Google
// sign-in for any account missing a role (new signups, or existing accounts backfilled this
// session). Phone step is now skipped automatically if a phone was already migrated/linked.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import BottomNav from '../components/BottomNav';
const MSG91_WIDGET_ID = '36676469546e333134393136';
const MSG91_TOKEN_AUTH = '547390TKaZu5sG16a48d72dP1';
export default function Login() {
  const { user, loginWithGoogle, saveWhatsappNumber, loginWithPhoneToken, setRole } = useStore();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [showOtpBlock, setShowOtpBlock] = useState(false);
  const [otpPhase, setOtpPhase] = useState('enter-phone');
  const [otpPhone, setOtpPhone] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const widgetReady = useRef(false);
  useEffect(() => {
    if (widgetReady.current) return;
    window.__msg91Config = {
      widgetId: MSG91_WIDGET_ID,
      tokenAuth: MSG91_TOKEN_AUTH,
      exposeMethods: true,
      success: () => {},
      failure: (err) => {
        console.error('MSG91 widget-level failure:', err);
      },
    };
    (function loadOtpScript(urls) {
      let i = 0;
      function attempt() {
        const s = document.createElement('script');
        s.src = urls[i];
        s.async = true;
        s.onload = () => {
          if (typeof window.initSendOTP === 'function') {
            window.initSendOTP(window.__msg91Config);
            widgetReady.current = true;
          }
        };
        s.onerror = () => {
          i++;
          if (i < urls.length) attempt();
        };
        document.head.appendChild(s);
      }
      attempt();
    })(['https://verify.msg91.com/otp-provider.js', 'https://verify.phone91.com/otp-provider.js']);
  }, []);
  async function handleGoogleSignIn() {
    setError('');
    setBusy(true);
    try {
      const result = await loginWithGoogle();
      if (!result.role) {
        setShowRoleSelector(true);
      } else if (!result.phone) {
        setShowPhonePrompt(true);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError('Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  async function handleSelectRole(role) {
    setError('');
    setBusy(true);
    const result = await setRole(role);
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setShowRoleSelector(false);
    if (!user?.phone) {
      setShowPhonePrompt(true);
    } else {
      navigate('/');
    }
  }
  async function handleSavePhone() {
    setError('');
    if (!phoneInput.trim() || phoneInput.trim().length < 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    setBusy(true);
    const result = await saveWhatsappNumber(phoneInput.trim());
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    navigate('/');
  }
  function handleSendOtp() {
    setOtpError('');
    setNotFound(false);
    if (!otpPhone.trim() || otpPhone.trim().length < 10) {
      setOtpError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!window.sendOtp) {
      setOtpError('OTP service not ready yet. Please wait a moment and try again.');
      return;
    }
    setOtpBusy(true);
    const identifier = '91' + otpPhone.trim().replace(/\D/g, '').slice(-10);
    window.sendOtp(
      identifier,
      () => {
        setOtpBusy(false);
        setOtpPhase('enter-otp');
      },
      (err) => {
        console.error('MSG91 sendOtp failure:', err);
        setOtpBusy(false);
        setOtpError('Could not send OTP. Please try again.');
      }
    );
  }
  function handleVerifyOtp() {
    setOtpError('');
    setNotFound(false);
    if (!otpValue.trim() || otpValue.trim().length !== 6) {
      setOtpError('Please enter the 6-digit code');
      return;
    }
    if (!window.verifyOtp) {
      setOtpError('OTP service not ready. Please try again.');
      return;
    }
    setOtpBusy(true);
    window.verifyOtp(
      otpValue.trim(),
      async (data) => {
        const accessToken = data?.message;
        if (!accessToken) {
          setOtpBusy(false);
          setOtpError('Verification failed. Please try again.');
          return;
        }
        const result = await loginWithPhoneToken(accessToken);
        setOtpBusy(false);
        if (result.status === 'ok') {
          navigate('/');
        } else if (result.status === 'not_found') {
          setNotFound(true);
        } else {
          setOtpError(result.error || 'Verification failed. Please try again.');
        }
      },
      (err) => {
        console.error('MSG91 verifyOtp failure:', err);
        setOtpBusy(false);
        setOtpError('Incorrect OTP. Please try again.');
      }
    );
  }
  if (user && showRoleSelector) {
    const roles = [
      { key: 'Customer', label: 'Customer', desc: 'Shop for yourself — earn loyalty points & nb credits' },
      { key: 'Reseller', label: 'Reseller', desc: 'Share products with your network and earn on every sale' },
      { key: 'Wholesaler', label: 'Wholesaler', desc: 'Bulk buyer — separate pricing and catalog' },
    ];
    return (
      <div className="app-shell">
        <nav style={{ background: '#0D1117', padding: '14px 18px', display: 'flex', justifyContent: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.08em' }}>Almost there</span>
        </nav>
        <div style={{ background: 'var(--navy)', padding: '32px 18px 40px' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--off-white)', marginBottom: 6 }}>
            How will you shop with us?
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            You can always access more options later — this doesn't lock you in
          </div>
        </div>
        <div className="page-body" style={{ paddingBottom: 100 }}>
          {error && (
            <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 12, padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>
              {error}
            </div>
          )}
          {roles.map(r => (
            <button
              key={r.key}
              onClick={() => handleSelectRole(r.key)}
              disabled={busy}
              style={{
                width: '100%', textAlign: 'left', background: '#fff',
                border: '0.5px solid rgba(27,42,74,0.15)', borderRadius: 8,
                padding: '14px 16px', marginBottom: 10, cursor: 'pointer', fontFamily: 'var(--font)',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--navy)', marginBottom: 3 }}>{r.label}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{r.desc}</div>
            </button>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }
  if (user && showPhonePrompt) {
    return (
      <div className="app-shell">
        <nav style={{ background: '#0D1117', padding: '14px 18px', display: 'flex', justifyContent: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.08em' }}>Almost there</span>
        </nav>
        <div style={{ background: 'var(--navy)', padding: '32px 18px 40px' }}>
          <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--off-white)', marginBottom: 6 }}>
            Welcome, {user.name?.split(' ')[0] || 'there'}!
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            Add your mobile number to continue — this is how you'll log in next time
          </div>
        </div>
        <div className="page-body" style={{ paddingBottom: 100 }}>
          {error && (
            <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 12, padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="login-field">
            <label>Mobile number</label>
            <input
              type="tel"
              placeholder="10-digit mobile number"
              value={phoneInput}
              onChange={e => setPhoneInput(e.target.value)}
            />
          </div>
          <button className="btn-primary" onClick={handleSavePhone} disabled={busy}>
            {busy ? 'Saving…' : 'Save & continue'}
          </button>
        </div>
        <BottomNav />
        <style>{`
          .login-field { margin-bottom: 14px; }
          .login-field label { display: block; font-size: 11px; color: var(--muted); margin-bottom: 5px; letter-spacing: 0.04em; }
          .login-field input { width: 100%; padding: 12px 14px; border: 0.5px solid rgba(27,42,74,0.2); border-radius: 6px; font-size: 14px; color: var(--navy); background: #fff; font-family: var(--font); outline: none; }
          .login-field input:focus { border-color: var(--blue); }
        `}</style>
      </div>
    );
  }
  return (
    <div className="app-shell">
      <nav style={{ background: '#0D1117', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button className="nav-icon" onClick={() => navigate(-1)} aria-label="Back">
          <i className="ti ti-arrow-left"></i>
        </button>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.08em' }}>Sign in</span>
        <div style={{ width: 20 }}></div>
      </nav>
      <div style={{ background: 'var(--navy)', padding: '32px 18px 40px' }}>
        <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 6 }}>
          nobrandstore
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Sign in to your account</div>
      </div>
      <div className="page-body" style={{ paddingBottom: 100 }}>
        {error && (
          <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 12, padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>
            {error}
          </div>
        )}
        <button
          type="button"
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}
          onClick={handleGoogleSignIn}
          disabled={busy}
        >
          <i className="ti ti-brand-google"></i>
          {busy ? 'Signing in…' : 'Continue with Google'}
        </button>
        <p style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.5, marginBottom: 14 }}>
          By continuing, you agree to our <Link to="/privacy" style={{ color: 'var(--blue)' }}>Privacy Policy</Link>.
        </p>
        {!showOtpBlock && (
          <button
            type="button"
            onClick={() => setShowOtpBlock(true)}
            style={{ width: '100%', background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)', padding: '4px 0 16px', textAlign: 'center' }}
          >
            Already have an account? Login with mobile number
          </button>
        )}
        {showOtpBlock && (
          <div style={{ marginTop: 4 }}>
            {otpError && (
              <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 12, padding: '10px 14px', borderRadius: 6, marginBottom: 14 }}>
                {otpError}
              </div>
            )}
            {notFound && (
              <div style={{ background: '#EEF4FF', border: '0.5px solid rgba(47,111,237,0.2)', color: 'var(--navy)', fontSize: 12, padding: '12px 14px', borderRadius: 6, marginBottom: 14, lineHeight: 1.6 }}>
                No account found for this number. Please sign up with Google above.
              </div>
            )}
            {otpPhase === 'enter-phone' && (
              <>
                <div className="login-field">
                  <label>Mobile number</label>
                  <input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={otpPhone}
                    onChange={e => setOtpPhone(e.target.value)}
                  />
                </div>
                <button className="btn-outline" style={{ marginBottom: 4 }} onClick={handleSendOtp} disabled={otpBusy}>
                  {otpBusy ? 'Sending…' : 'Send OTP'}
                </button>
              </>
            )}
            {otpPhase === 'enter-otp' && (
              <>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                  Code sent to {otpPhone}
                </div>
                <div className="login-field">
                  <label>Enter 6-digit OTP</label>
                  <input
                    type="tel"
                    maxLength={6}
                    placeholder="000000"
                    value={otpValue}
                    onChange={e => setOtpValue(e.target.value)}
                  />
                </div>
                <button className="btn-outline" style={{ marginBottom: 4 }} onClick={handleVerifyOtp} disabled={otpBusy}>
                  {otpBusy ? 'Verifying…' : 'Verify & Login'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="page-body" style={{ paddingTop: 0 }}>
        <div style={{ background: '#EEF4FF', borderRadius: 10, padding: 16, border: '0.5px solid rgba(47,111,237,0.2)' }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--blue)', marginBottom: 6 }}>
            Are you a reseller?
          </div>
          <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, marginBottom: 10 }}>
            Use the Share & Earn feature on any product page to share products and earn your margin. No registration needed.
          </div>
          <button
            onClick={() => navigate('/shop')}
            style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)', padding: 0 }}
          >
            Browse products →
          </button>
        </div>
      </div>
      <BottomNav />
      <style>{`
        .login-field { margin-bottom: 14px; }
        .login-field label { display: block; font-size: 11px; color: var(--muted); margin-bottom: 5px; letter-spacing: 0.04em; }
        .login-field input { width: 100%; padding: 12px 14px; border: 0.5px solid rgba(27,42,74,0.2); border-radius: 6px; font-size: 14px; color: var(--navy); background: #fff; font-family: var(--font); outline: none; }
        .login-field input:focus { border-color: var(--blue); }
      `}</style>
    </div>
  );
}